import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { api, type Release } from '../api';
import { useMe, useReleases } from '../hooks';
import { formatImported } from '../shared/format';
import { modules } from '../modules/registry';
import { activeModuleId, activeTab, mainClassForPath, visibleModules } from '../modules/nav';
import Splash from './Splash';
import { PERSONA_LABEL, canEditResume, isManager } from '../shared/persona';

const DISMISS_KEY = 'ccs.release.dismissed';

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function readDismissed(): string {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) ?? '';
  } catch {
    return '';
  }
}

function ReleaseToast({ latest, unseen }: { latest: Release | null; unseen: number }) {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(readDismissed);
  const key = latest ? String(latest.id) : '';

  useEffect(() => {
    if (!latest || unseen <= 0 || dismissed === key) return undefined;
    const timer = window.setTimeout(() => {
      setDismissed(key);
      try {
        window.sessionStorage.setItem(DISMISS_KEY, key);
      } catch {
        // ignore storage errors
      }
    }, 12_000);
    return () => window.clearTimeout(timer);
  }, [latest, unseen, dismissed, key]);

  if (!latest || unseen <= 0 || dismissed === key || location.pathname === '/releases') {
    return null;
  }

  const dismiss = () => {
    setDismissed(key);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, key);
    } catch {
      // ignore storage errors
    }
  };

  return (
    <div className="release-toast" role="status">
      <div className="release-toast-mark">NEW</div>
      <div className="release-toast-body">
        <b>新版本已上线</b>
        <div className="release-toast-title">
          {latest.version} · {latest.title}
        </div>
        <div className="release-toast-meta">
          {formatImported(latest.deployedAt)}
          {latest.stats.commits > 0 ? ` · ${latest.stats.commits} 次提交` : ''}
          {` · ${latest.stats.files} 个文件`}
        </div>
        <Link to="/releases" className="release-toast-link" onClick={dismiss}>
          查看发布日志 →
        </Link>
      </div>
      <button
        type="button"
        className="release-toast-close"
        onClick={dismiss}
        aria-label="关闭上线提示"
      >
        ×
      </button>
    </div>
  );
}

export default function AppShell() {
  const me = useMe();
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = me.data ?? null;
  const releases = useReleases(Boolean(user));
  const navRef = useRef<HTMLElement>(null);

  const closeMenus = () => {
    navRef.current?.querySelectorAll<HTMLDetailsElement>('details.nav-menu[open]').forEach((item) => {
      item.open = false;
    });
  };

  // 点空白处、按 Esc、切换页面时收起模块下拉，避免菜单一直盖住内容
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const inside = target.closest('details.nav-menu');
      navRef.current?.querySelectorAll<HTMLDetailsElement>('details.nav-menu[open]').forEach((item) => {
        if (item !== inside) item.open = false;
      });
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  useEffect(() => {
    closeMenus();
  }, [location.pathname]);

  if (me.isLoading) return <Splash />;

  if (user?.must_change_password && location.pathname !== '/account/password') {
    return <Navigate to="/account/password" replace />;
  }

  const logout = async () => {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    queryClient.clear();
    window.location.href = '/login';
  };

  const visible = visibleModules(modules, user?.persona ?? 'visitor');
  const currentModuleId = activeModuleId(visible, location.pathname);
  const currentModule = visible.find((module) => module.id === currentModuleId) ?? null;
  const currentTab = currentModule ? activeTab(currentModule, location.pathname) : null;
  const tabs = currentModule?.tabs ?? [];
  const mainClass = mainClassForPath(visible, location.pathname);
  const unseen = releases.data?.unseenCount ?? 0;
  const latestRelease = releases.data?.latest ?? releases.data?.releases[0] ?? null;

  return (
    <div className="shell">
      <ScrollToTop />
      <header className="topbar">
        <Link to="/" className="brand">
          <img className="brand-mark" src="/brand/lab-emblem.png" alt="" />
          <div>
            碳捕集与能源利用数智实验室
            <small>CEUS · Digital Smart Lab</small>
          </div>
        </Link>
        <nav className="nav" ref={navRef}>
          <NavLink to="/" end className={({ isActive }) => (isActive ? 'active' : '')}>
            首页
          </NavLink>
          <Link to="/#research" className="nav-plain">
            研究方向
          </Link>
          {visible.map((module) =>
            module.tabs?.length ? (
              <details key={module.id} className="nav-menu">
                <summary className={currentModuleId === module.id ? 'active' : ''}>
                  {module.menuLabel ?? module.title}
                </summary>
                <div className="nav-menu-panel">
                  {module.tabs.map((tab) => (
                    <Link
                      key={tab.to}
                      to={tab.to}
                      className={currentTab?.to === tab.to ? 'active' : ''}
                      onClick={(event) => {
                        const menu = event.currentTarget.closest('details');
                        if (menu) menu.open = false;
                      }}
                    >
                      {tab.label}
                    </Link>
                  ))}
                </div>
              </details>
            ) : (
              <NavLink
                key={module.id}
                to={`/${module.id}`}
                end
                className={({ isActive }) => (isActive ? 'active' : '')}
              >
                {module.title}
              </NavLink>
            ),
          )}
          {user ? (
            <>
              <NavLink to="/releases" className={({ isActive }) => (isActive ? 'active' : '')}>
                发布日志
                {unseen > 0 ? (
                  <span className="nav-badge" aria-label={`${unseen} 条未读发布`}>
                    {unseen > 9 ? '9+' : unseen}
                  </span>
                ) : null}
              </NavLink>
              <NavLink to="/resumes" className={({ isActive }) => (isActive ? 'active' : '')}>
                课题组简历
              </NavLink>
              {canEditResume(user.persona) ? (
                <NavLink
                  to="/account/resume"
                  className={({ isActive }) => (isActive ? 'active' : '')}
                >
                  我的简历
                </NavLink>
              ) : null}
              {isManager(user.persona) ? (
                <>
                  <NavLink
                    to="/admin/users"
                    className={({ isActive }) => (isActive ? 'active' : '')}
                  >
                    用户
                  </NavLink>
                  <NavLink
                    to="/ccs/admin/devices"
                    className={({ isActive }) => (isActive ? 'active' : '')}
                  >
                    设备
                  </NavLink>
                  <NavLink
                    to="/admin/resumes"
                    className={({ isActive }) => (isActive ? 'active' : '')}
                  >
                    简历审核
                  </NavLink>
                </>
              ) : null}
              <NavLink
                to="/account/password"
                className={({ isActive }) => (isActive ? 'active' : '')}
              >
                账号
              </NavLink>
            </>
          ) : null}
        </nav>
        {user ? (
          <div className="user">
            <div className="avatar">{user.display_name.slice(0, 1)}</div>
            <div>
              <div className="user-name">{user.display_name}</div>
              <div className="user-role">{PERSONA_LABEL[user.persona]}</div>
            </div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={logout}>
              退出
            </button>
          </div>
        ) : (
          <div className="user">
            <Link to="/login" className="btn btn-primary btn-sm">
              登录
            </Link>
          </div>
        )}
      </header>
      {tabs.length ? (
        <nav className="module-tabs" aria-label={`${currentModule?.title ?? ''} 功能`}>
          {tabs.map((tab) => (
            <Link
              key={tab.to}
              to={tab.to}
              className={`module-tab${currentTab?.to === tab.to ? ' active' : ''}`}
            >
              {tab.label}
            </Link>
          ))}
        </nav>
      ) : null}
      <main className={`page${mainClass ? ` ${mainClass}` : ''}`}>
        <Outlet />
      </main>
      <footer className="footer">
        <span>© 2026 碳捕集与能源利用数智实验室 · CCS 采集门户</span>
        <span>
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer">
            沪ICP备19044375号-1
          </a>
        </span>
      </footer>
      {user ? <ReleaseToast latest={latestRelease} unseen={unseen} /> : null}
    </div>
  );
}